import {
  lazy,
  Suspense,
  useEffect,
  type ComponentType,
  type LazyExoticComponent,
} from "react";

import { ConsentBar } from "@/components/layout/ConsentBar";
import { usePageView } from "@/hooks/usePageView";
import { usePageExit } from "@/hooks/usePageExit";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { useSectionViews } from "@/hooks/useSectionViews";
import { useOutboundClicks } from "@/hooks/useOutboundClicks";
import { useStoreCampaignLinks } from "@/hooks/useStoreCampaignLinks";
import { useUiClicks } from "@/hooks/useUiClicks";
import { track } from "@/lib/analytics";
import { consumeChunkReload, routeChunk } from "@/lib/chunk-reload";

import { App } from "./App";

// Public HTML is generated by App.server.tsx. The browser only fetches the
// route module it needs, while Vite keeps each page and its unique visual
// dependencies in a cacheable async chunk. The null fallback is intentional:
// it never replaces the prerendered page with a loading message or fake shell.
//
// EACH PAGE ALSO OWNS ITS STYLESHEET now (2026-09-06). A route-specific sheet
// is imported by the page or component that draws with it rather than by
// src/index.css, so it rides this chunk instead of the one render-blocking file
// every route downloads. That makes the chunk boundary a CSS boundary too, and
// artifacts/web/manifest-css.mjs is the map from a route to the module below
// that serves it: prerender.mjs reads that map to put the sheet's <link> in the
// raw <head> (without it the prerendered page paints unstyled), and
// verify-crawl.mjs reads it to assert the link is really in the bytes. Its
// drift guard fails the build if this list and that map stop naming the same
// pages — so a page added, renamed or removed here is edited in BOTH places.
// A page chunk that fails to load reloads the page once, and only a page
// chunk may: see lib/chunk-reload.ts. Every route below goes through this.
function page<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => routeChunk(load));
}

const Home = page(() => import("@/pages/home"));
const Features = page(() => import("@/pages/features"));
const Demo = page(() => import("@/pages/demo"));
const Insights = page(() => import("@/pages/insights"));
const StudyLab = page(() => import("@/pages/study-lab"));
const Programs = page(() => import("@/pages/programs"));
const Evidence = page(() => import("@/pages/evidence"));
const BestCaseLogApp = page(() => import("@/pages/best-case-log-app"));
const AccuracyTest = page(() => import("@/pages/accuracy-test"));
const Ambassadors = page(() => import("@/pages/ambassadors"));
const About = page(() => import("@/pages/about"));
const Pricing = page(() => import("@/pages/pricing"));
const Reimbursement = page(() => import("@/pages/reimbursement"));
const Privacy = page(() => import("@/pages/privacy"));
const Security = page(() => import("@/pages/security"));
const Terms = page(() => import("@/pages/terms"));
const Download = page(() => import("@/pages/download"));
const Support = page(() => import("@/pages/support"));
const Feedback = page(() => import("@/pages/feedback"));
const Changelog = page(() => import("@/pages/changelog"));
const Press = page(() => import("@/pages/press"));
const Sucks = page(() => import("@/pages/sucks"));
const HelpIndex = page(() => import("@/pages/help/index"));
const HelpArticlePage = page(() => import("@/pages/help/article"));
const Join = page(() => import("@/pages/join"));
const SpecialtyPage = page(() => import("@/pages/specialty"));
const ComingSoonSpecialtyPage = page(
  () => import("@/pages/specialty-coming-soon"),
);
const AudiencePage = page(() => import("@/pages/audience"));
const ComparePage = page(() => import("@/pages/compare"));
const AcgmeMinimumsIndex = page(() => import("@/pages/acgme-minimums-index"));
const AcgmeMinimumsPage = page(() => import("@/pages/acgme-minimums"));
const NotFound = page(() => import("@/pages/not-found"));

// A DRAFT SHIPS IN NO PRODUCTION FILE (E-364). While no post is published, a
// production build never imports the blog pages, so Vite emits no chunk that
// holds a draft's text, and /blog falls through to NotFound as before. The dev
// server keeps them, so the owner can read a draft locally.
declare const __BLOG_LIVE__: boolean;
const BlogIndex =
  __BLOG_LIVE__ || import.meta.env.DEV
    ? page(() => import("@/pages/blog/index"))
    : NotFound;
const BlogPostPage =
  __BLOG_LIVE__ || import.meta.env.DEV
    ? page(() => import("@/pages/blog/post"))
    : NotFound;

// The advertorials (D-229) follow the blog's rule: while none is live, a
// production build never imports the page, so no chunk holds a draft's text.
// The flag is vite.config.ts's, read off LIVE_ADVERTORIAL_ROUTE_SLUGS.
declare const __ADVERTORIALS_LIVE__: boolean;
const AdvertorialPage =
  __ADVERTORIALS_LIVE__ || import.meta.env.DEV
    ? page(() => import("@/pages/advertorial"))
    : NotFound;

// Inside the Suspense boundary, so its effect runs only when the route chunk has
// loaded and the page has hydrated. Renders nothing, so no hydration mismatch.
function RouteRendered() {
  useEffect(() => {
    if (consumeChunkReload()) track("chunk_reload_recovered");
  }, []);
  return null;
}

export default function ClientApp() {
  // Analytics lives HERE, not in App.tsx: App.tsx is shared with
  // entry-server.tsx and runs in Node during the prerender pass, where there is
  // no window to measure. This component is client-only by construction
  // (main.tsx is its only importer), and it renders nothing of its own, so it
  // cannot introduce a hydration mismatch. See docs/ops/web-funnel.md.
  const template = usePageView();
  useScrollDepth(template);
  useOutboundClicks();
  // Tags every App Store link with Apple's campaign token at the moment it is
  // pressed, so App Store Connect can split downloads by channel. Inert until
  // BRAND.appStoreProviderToken is set. See docs/ops/campaign-links.md.
  useStoreCampaignLinks();
  // The detail lane (2026-09-05): which bands were read and for how long, every
  // CTA and nav item by name, FAQ opens, time to first interaction, and how far
  // down the page the visitor was when they left. See docs/ops/web-funnel.md §2.
  useSectionViews(template);
  useUiClicks(template);
  usePageExit(template);

  return (
    <Suspense fallback={null}>
      {/* Outside <App> so it survives every route change, and last in the tree
          so it paints over the page rather than inside a band. */}
      <ConsentBar />
      <RouteRendered />
      <App
        components={{
          Home,
          Features,
          Demo,
          Insights,
          StudyLab,
          Programs,
          Evidence,
          BestCaseLogApp,
          AccuracyTest,
          Ambassadors,
          About,
          Pricing,
          Reimbursement,
          Privacy,
          Security,
          Terms,
          Download,
          Support,
          Feedback,
          Changelog,
          Press,
          Sucks,
          HelpIndex,
          HelpArticlePage,
          BlogIndex,
          BlogPostPage,
          AdvertorialPage,
          Join,
          SpecialtyPage,
          ComingSoonSpecialtyPage,
          AudiencePage,
          ComparePage,
          AcgmeMinimumsIndex,
          AcgmeMinimumsPage,
          NotFound,
        }}
      />
    </Suspense>
  );
}
